import { OrderEvent, OrderEventField } from '../../../types/order/OrderEvent';
import OrderEventType from '../../../types/order/OrderEventType';

const isBlank = (value?: string) => !value || value === '' || value === 'null';

const getChangeEntries = (event: OrderEvent): OrderEventField[] => {
    if (event.eventType === OrderEventType.OrderInvoiceAddressChanged)
        return event.metaData.invoiceAddressChanges || [];
    return event.metaData.purchaseMetadataChanges || [];
};

const fieldNameMap: Record<string, string> = {
    MobilePhone: 'Mobile phone',
    PhoneNumber: 'Phone number',
    PeppolId: 'Peppol ID',
    ElectronicInvoiceReceiverAddress: 'Finvoice address',
    ElectronicInvoiceReceiverIntermediatorAddress: 'Finvoice address',
};

export const formatFieldName = (name?: string) => {
    if (!name) return '';
    return fieldNameMap[name] ?? name;
};

const finvoiceFields = [
    'ElectronicInvoiceReceiverAddress',
    'ElectronicInvoiceReceiverIntermediatorAddress',
];

const isFinvoice = (e: OrderEventField) => finvoiceFields.includes(e.name ?? '');

const isFinvoiceEvent = (entries: OrderEventField[]) =>
    finvoiceFields.every(name => entries.some(e => e.name === name));

const simpleFieldLabel = (fieldName: string, countryCode?: string): string => {
    if (countryCode === 'NO' && fieldName === 'PeppolId') return 'EHF';

    return formatFieldName(fieldName);
};

const getFinvoiceSummary = (entries: OrderEventField[]): string | null => {
    const intermediator =
        entries.find(e => e.name === 'ElectronicInvoiceReceiverIntermediatorAddress')?.newValue ??
        '';
    const address =
        entries.find(e => e.name === 'ElectronicInvoiceReceiverAddress')?.newValue ?? '';
    if (isBlank(intermediator) && isBlank(address)) return null;
    return `Finvoice address set to ${intermediator};${address}`;
};

const formatFinvoiceChangeFields = (entries: OrderEventField[]): OrderEventField[] => {
    const [address, intermediator] = finvoiceFields.map(name => entries.find(e => e.name === name));
    if (!intermediator || !address) return entries;

    return [
        ...entries.filter(e => !isFinvoice(e)),
        {
            ...address,
            oldValue: [intermediator.oldValue, address.oldValue].filter(Boolean).join(';') || '',
            newValue: [intermediator.newValue, address.newValue].filter(Boolean).join(';') || '',
        },
    ];
};

export const formatChangeFields = (
    entries: OrderEventField[]
): OrderEventField[] => {
    if (!isFinvoiceEvent(entries)) return entries;

    return formatFinvoiceChangeFields(entries);
};

export const getSimpleChangeSummary = (event: OrderEvent, countryCode?: string): string | null => {
    const entries = getChangeEntries(event);
    const meaningful = entries.filter(e => !isBlank(e.newValue));

    const hasFinvoice = meaningful.some(isFinvoice);

    if (hasFinvoice) return getFinvoiceSummary(entries);

    if (meaningful.length !== 1) return null;
    const [entry] = meaningful;
    const label = simpleFieldLabel(entry.name ?? '', countryCode);
    return `${label} set to ${entry.newValue}`;
};

export const isAddressOrMetadataChangeEvent = (event: OrderEvent) =>
    event.eventType === OrderEventType.OrderInvoiceAddressChanged ||
    event.eventType === OrderEventType.ElectronicInvoiceChanged ||
    event.eventType === OrderEventType.InvoiceInformationUpdated;

export const shouldHideChangeEvent = (event: OrderEvent): boolean => {
    if (!isAddressOrMetadataChangeEvent(event)) return false;

    const entries = getChangeEntries(event);
    return entries.length > 0 && entries.every(e => isBlank(e.newValue));
};
